/* THE STORE — the single mutable app state.

   This module imports nothing app-shaped, on purpose. Almost every other
   module reads or writes S, so if it had imports of its own it would sit in
   the middle of an import cycle. ESM tolerates cycles, but a `const` caught
   in the temporal dead zone surfaces as "undefined is not a function" at
   boot with a stack that points nowhere useful. Keeping this file (and
   bus.js) as leaves makes that class of bug impossible — the one exception
   is core/defaults.js and core/util.js below, which are themselves leaves
   (import nothing) and so can never be the module that closes a cycle back
   here.

   Units are metres and degrees. The screen plane is z = 0, X right, Y up, and
   the viewer stands at +Z. */
import { projectDefaults } from '../core/defaults.js';

const D = projectDefaults();

export const S = {
  stage: 'calib',             // calib | compose | output
  // orbit | flat | site | split (split: manual/no-photo setup only) | null (not yet resolved — see ui/chrome.js's goStage)
  view: null,

  photo: null,                // {url, w, h}
  corners: D.corners,

  screen: D.screen,
  res: D.res,

  focal: { px: null, mode: D.focalMode, manualMM: D.focalManualMM, method: '' },
  eye: null,                  // solved eye, THREE.Vector3, metres
  nudge: D.nudge,
  solved: false,
  seeded: false,

  objects: [],
  selId: null,
  // The eye/camera "selected" the same way a real object is — its own
  // gizmo shows, driving S.nudge instead of a real o.pos. Not a real
  // object, so it can't live in selId; cleared alongside it in
  // scene/objects.js's select() so the two are never both active.
  eyeSelected: false,
  /* The SCREEN ARRAY, selected the way the eye above is: it shows in the
     outliner and owns an inspector, but it is not in S.objects and never will
     be. It is not something you add — there is no tool for it, because it is
     not content — and it has no position, no material and nothing to render;
     it IS the screen, and S.screenArray already holds it. A real object would
     have to be excluded from the export, from rescaleForScreen, from duplicate
     and delete, and from the document's object list, which is a lot of special
     cases to buy a row in a list. Like eyeSelected, it cannot live in selId, and
     the three are never simultaneously active — scene/objects.js's select()
     clears this one. */
  arraySelected: false,

  gizmo: 'move',              // move | rotate | scale
  gizmoSpace: 'world',
  gizmoSize: D.gizmoSize,

  /* A CORNER SCREEN's second panel, or null for the ordinary single screen —
     which is every project that existed before this, and the default.

     S.screen / S.res / S.corners keep meaning the FIRST panel exactly as they
     always have, so nothing that reads them needs to know corners exist. What
     this adds is only what is genuinely new: the second panel's width and
     horizontal resolution (height is shared, because the panels share an edge),
     and the two marks on its far edge.

     The shared edge is deliberately NOT stored here. It is S.corners.tr and
     S.corners.br — the first panel's right edge IS the second panel's left
     edge, one entity with one copy, so the two can never drift apart and
     nothing has to keep them in step.

     `convex` is derived by the solve (core/corner.js reads it off the geometry)
     rather than typed, so it is a cache of a measurement, not a setting. */
  cornerPanel: null,          // {w, resW, outer:{tr:{x,y}, br:{x,y}}, convex}

  /* AN ARRAY OF SCREENS — a video wall of cols x rows displays with a physical
     margin between them — or null for the ordinary single screen.

     THE WALL IS THE SCREEN. S.screen keeps meaning "the surface the illusion is
     designed for", which for an array is the whole wall INCLUDING its gaps, and
     S.res is that wall at the tiles' own pixel pitch. Everything downstream then
     needs to know nothing: refitRecess auto-fills the inset to the wall, so one
     inset spanning every screen falls out for free, and rescaleForScreen,
     lighting and the Flat camera all keep reading one rectangle.

     What an array actually is, then, is only two things: a PARTITION of that
     rectangle for export (one file per screen, scene/panels.js's panelDescs)
     and a guide in the working view. Both derive from these numbers, so there
     is no second copy of the wall to fall out of step with S.screen.

     Mutually exclusive with cornerPanel: a grid that also folds is a much
     larger feature (every tile would need its own fold state), so setting
     either clears the other. */
  screenArray: null,          // {cols, rows, gap, tile:{w,h}, res:{w,h}}

  cornersLocked: false,
  siteZoom: 1, sitePan: { x: 0, y: 0 },
  /* On-site only, and a different axis from siteZoom above: that magnifies the
     finished composite, this widens the FRUSTUM the working view renders
     through, so objects outside the screen's own viewing cone — a key light
     above the sign, most obviously — are drawn against the surrounding
     photograph where they actually are, and can be grabbed there.
     1 = off, and the export never sees it: scene/export/frame.js calls
     updateFlatCam() with no margin, so the deliverable is always the tight
     screen rect no matter what this says. */
  siteOverscan: 1,
  /* And whether it is allowed to happen at all. OFF, because the widening was
     automatic and therefore constant: it keys off the SELECTION, so every click
     between something on the screen and something off it resized the whole view
     and brought the dashed deliverable edge in and out with it. Reported as the
     view jumping and zooming on every click — and worst in Motion, where objects
     legitimately sit outside the frame waiting to fly in.

     Kept rather than deleted because the need behind it is real: a key light
     above the sign has to be grabbable somewhere. It is now asked for, in Scene
     Settings, which the outliner always offers — the reason a control was
     rejected the first time was that its old home was replaced by the inspector
     the moment anything was selected, and that is no longer where it lives.
     View state, like siteZoom: never written to the document. */
  siteReach: false,
  /* Whether On-site draws the selection outline and the null markers over the
     photograph. A way of looking, like siteReach, so not saved with the work. */
  siteOutlines: true,
  // Standalone Flat only — narrows updateFlatCam's off-axis frustum around
  // the screen rect's own centre (scene/cameras.js), a live preview aid the
  // same way orb.dist is for Orbit. The export always renders at 1 regardless.
  flatZoom: 1,
  /* Companion to flatZoom: shifts that same frustum sideways/vertically so a
     zoomed-in Flat view can be moved around, the way siteZoom has sitePan.
     In METRES on the screen plane, not pixels — it feeds the frustum
     directly rather than a CSS transform. Preview-only, like the zoom. */
  flatPan: { x: 0, y: 0 },

  nextId: 1,

  bg: D.bg,
  tone: D.tone,
  envInt: D.envInt,
  aoStrength: D.aoStrength,   // scene/ao.js
  aoReach: D.aoReach,
  dofStrength: D.dofStrength, // scene/dof.js
  dofFocus: { ...D.dofFocus },
  shadowRes: D.shadowRes,
  shadowSoft: D.shadowSoft,   // blur radius in shadow-map texels — see scene/lighting.js's setShadowSoft
  ss: D.ss,                   // supersample factor for the flat render
  motionBlur: D.motionBlur,   // sub-frames averaged per output frame, video export only. 1 = off
  previewQuality: true,       // apply that supersample factor live in Orbit/Flat too, not just at export
  // Screen frame + frustum in Orbit — a useful reference for understanding
  // the calibrated eye/viewing cone, but mostly clutter once you're deep
  // into composing objects and already trust the calibration. Off by
  // default; the checkbox (Master Settings' Light & stage panel) is what
  // brings it back for whoever actually wants it.
  showHelpers: false,
  /* THE BEZELS between an array's screens: how they are shown, not how wide
     they are. The width is S.screenArray.gap, which is the installation
     itself; these three are how you want to look at it, and all three travel
     with the project — see the note beside them in core/defaults.js for why
     "cannot reach the deliverable" turned out to be the wrong test. None of
     them can, and neither can gizmoSize. */
  showBezels: D.showBezels,
  bezelOpacity: D.bezelOpacity,
  bezelPhoto: D.bezelPhoto,
  onsite: { ...D.onsite },
  // A flat, camera-facing reference grid over a corner screen's shared
  // seam, On-site only — see scene/calib-grid.js. It never touches the
  // per-panel anamorphic warp itself, so it can't repeat whatever doSolve
  // got wrong the way a grid drawn on the panels' own planes would; any
  // skew it shows is the calibration's, not its own. Off by default; the
  // toggle lives top-right of the On-site canvas (ui/site-view.js).
  showCalibGrid: false,
  /* On-site only: pin the second panel where it PROJECTS rather than where it
     was marked, so the seam closes exactly and any disagreement between the
     marks and the geometry shows up at that panel's outline instead. See
     ui/site-view.js's projectedCornerQuad. Never reaches an export.

     ON by default. The seam is the join the whole illusion is built around,
     and a step there reads as broken in a way a slightly-off outline does not
     — the outline is judged against a building edge nobody is looking at,
     the seam against the continuity of the piece itself. Where the marks and
     the geometry agree the two quads coincide and this changes nothing at
     all; where they disagree it puts the difference on the outline, which is
     the cheaper place for it. The button turns it off for the case where the
     marks are the thing you trust. */
  siteSeamExact: true,
  lighting: D.lighting,
  // Overrides for the preset's own key-light direction, in degrees. null means
  // "use this preset's built-in direction" — see scene/lighting.js's
  // effectiveLightAzEl(). Resetting sets both back to null rather than to a
  // remembered number, so switching presets shows that preset's own default.
  lightAz: null, lightEl: null,

  /* Fonts the user uploaded with THIS project: [{family, weight, src|assetId}].
     Document level rather than per object, because several text objects share
     one face. Registered into document.fonts before the scene is applied. */
  fonts: [],

  /* The animation timeline — see core/anim.js for the keyframe model, which
     lives on the objects themselves rather than here. */
  timeline: D.timeline,           // {duration seconds, fps}
  /* Where the playhead sits, in seconds. Deliberately NOT part of the
     document or of the undo snapshot: scrubbing is looking, not editing, and
     a timeline that filled the undo stack with fifty entries every time
     someone dragged across it would make undo useless for the edits that
     actually matter. */
  playhead: 0,
  playing: false,

  /* Unsaved changes since the last write to the cloud. Only meaningful when a
     cloud project is open; the local .json path saves on demand. */
  dirty: false
};

/* ── small shared utilities ─────────────────────────────────────────────────
   These live here rather than in a utils.js because they are needed almost
   everywhere and, like S, must not pull in dependencies. */

export const $ = s => document.querySelector(s);

export const el = (t, c, txt) => {
  const n = document.createElement(t);
  if (c) n.className = c;
  if (txt != null) n.textContent = txt;
  return n;
};

/* Does this element do something meaningful with a keystroke, or is it merely
   where focus happens to be sitting?

   Here rather than in either caller because BOTH viewport shortcuts and the
   timeline's own Delete need it, and they are on opposite sides of an import
   that would otherwise become a cycle. Text entry, selects, checkboxes and
   radios keep their keys; a number, range or colour field does not — a space
   or a Delete means nothing to those, so guarding them only cost the
   shortcut. */
/* The exception to the rule below, and it is per-KEY rather than per-element.
   typingTarget says a number, range or colour field does not keep keys as a
   class, and for a space or a letter that is right — none of them mean
   anything to such a field, so the viewport shortcut has the better claim.
   The editing keys are a different matter entirely: Backspace and Delete
   change the value under the cursor, Up/Down are the field's own spinner,
   Left/Right and Home/End move within the number. Passing those through was
   destructive in a way passing a space through never is — Delete while
   correcting a size removed the whole OBJECT, and Up nudged it across the
   scene, both of them a surprise arriving from a field you were looking at.

   Colour is in here too. Nothing in the list does anything useful to a colour
   swatch, but "does nothing" is the correct outcome; deleting the object is
   not. */
const TYPES_THAT_OWN_EDIT_KEYS = new Set(['number', 'range', 'color']);
const KEYS_A_VALUE_FIELD_OWNS = new Set([
  'Backspace', 'Delete', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'
]);
export function fieldOwnsKey(elm, key) {
  return !!elm && elm.tagName === 'INPUT' &&
         TYPES_THAT_OWN_EDIT_KEYS.has((elm.type || '').toLowerCase()) &&
         KEYS_A_VALUE_FIELD_OWNS.has(key);
}

const TYPES_THAT_KEEP_KEYS = new Set(['checkbox', 'radio']);
export function typingTarget(elm) {
  if (!elm) return false;
  if (elm.isContentEditable) return true;
  const tag = elm.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag !== 'INPUT') return false;
  const type = (elm.type || 'text').toLowerCase();
  if (TYPES_THAT_KEEP_KEYS.has(type)) return true;
  return !(type === 'number' || type === 'range' || type === 'color');
}

/* Re-exported so the ~90 call sites here keep importing it from the store, while
   core/ can reach the same function without depending on app state. */
export { clamp } from '../core/util.js';

/** Trim float noise for display, and never print "-0". */
export const fmt = (v, d = 2) => (Math.abs(v) < 1e-9 ? 0 : v).toFixed(d);
